// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { parseBoolean } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import sendMessage from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SendMessage";
import translate from "@insite/client-framework/Translate";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import sortBy from "lodash/sortBy";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    oldQuoteState: state.xQuoteDetails.quote,
});

const mapDispatchToProps = {
    sendMessage,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsMessagesStyles {
    mainWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    messageTextArea?: TextAreaProps;
    sendButton?: ButtonPresentationProps;
    messagesContainer?: GridContainerProps;
    messageGridItem?: GridItemProps;
    userNameText?: TypographyPresentationProps;
    messageDateText?: TypographyPresentationProps;
    messageBodyText?: TypographyPresentationProps;
}

export const rfqQuoteDetailsMessagesStyles: RfqQuoteDetailsMessagesStyles = {
    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding-top: 30px;
            border-top: 3px solid ${getColor("common.ui3")};
        `,
    },
    titleText: {
        variant: "h2",
    },
    messageTextArea: {
        cssOverrides: {
            formField: css`
                margin-bottom: 15px;
                max-width: 895px;
                label {
                    font-weight: ${fontConfig.weight.semiBold};
                }
                @media print {
                    display: none;
                }
            `,
            inputSelect: css`
                resize: none;
            `,
        },
    },
    sendButton: {
        variant: "secondary",
        css: css`
            max-width: 200px;
            padding: 0 35px;
            @media print {
                display: none;
            }
        `,
    },
    messagesContainer: {
        gap: 0,
        css: css`
            ${GridOffset} {
                max-width: 895px;
                margin-top: 30px;
            }
            ${GridItemStyle} {
                padding: 30px 0;
                border-top: 1px solid ${getColor("common.ui2")};
            }
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
        `,
    },
    userNameText: {
        weight: fontConfig.weight.semiBold,
    },

    messageBodyText: {
        css: css`
            margin-top: 10px;
            width: 100%;
        `,
    },
};

const styles = rfqQuoteDetailsMessagesStyles;

const RfqQuoteDetailsMessages = ({ quoteState, oldQuoteState, sendMessage }: Props) => {
    const quote = useMemo(() => {
        return quoteState.value || oldQuoteState;
    }, [quoteState.value, oldQuoteState]);

    const [message, setMessage] = useState("");
    const messageChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.currentTarget.value);
    };

    const [sortedMessages, setSortedMessages] = useState<MessageModel[]>([]);
    useEffect(() => {
        if (!quote) {
            return;
        }

        setSortedMessages(sortBy(quote.messageCollection || [], ["createdDate"]).reverse());
    }, [quote?.messageCollection]);

    const sendClickHandler = () => {
        if (!quote) {
            return;
        }

        sendMessage({ quote, message });
        setMessage("");
    };

    if (!quote) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <Typography {...styles.titleText}>{translate("Messages")}</Typography>
            {!parseBoolean(quote.properties.isExpired) && (
                <>
                    <TextArea
                        {...styles.messageTextArea}
                        label={translate("Write a Message")}
                        placeholder={siteMessage("Rfq_GhostMessage") as string}
                        value={message}
                        onChange={messageChangeHandler}
                        data-test-selector="rfqQuoteDetails_MessageField"
                    />
                    <Button
                        {...styles.sendButton}
                        disabled={!message.trim()}
                        onClick={sendClickHandler}
                        data-test-selector="rfqQuoteDetails_SendMessageButton"
                    >
                        {translate("Send Message")}
                    </Button>
                </>
            )}
            <GridContainer {...styles.messagesContainer} data-test-selector="rfqQuoteDetails_Messages">
                {sortedMessages.map(message => (
                    <GridItem key={message.createdDate.toString()} {...styles.messageGridItem}>
                        <Typography {...styles.userNameText}>{message.displayName}</Typography>
                        <Typography color="common.ui2">&nbsp;&nbsp;|&nbsp;&nbsp;</Typography>
                        <Typography {...styles.messageDateText}>
                            <LocalizedDateTime
                                dateTime={message.createdDate}
                                options={{
                                    year: "numeric",
                                    month: "numeric",
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "numeric",
                                }}
                            />
                        </Typography>
                        <Typography {...styles.messageBodyText}>{message.body}</Typography>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsMessages),
    definition: {
        displayName: "Messages",
        allowedContexts: ["RfqQuoteDetailsPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
